import { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import Im from "immutable";
import Moment from "moment";

// export const CCP_API_URL = "http://localhost:8080/company";
export const CCP_API_URL =
  "https://x92k7u2n41.execute-api.eu-west-2.amazonaws.com/main/api/ccp/company";

const _isStr = (value) => typeof value === "string" || value instanceof String;

const sleep = (sec) => new Promise((r) => setTimeout(r, 1000 * sec));

const getToken = () => {
  return localStorage.getItem("token", undefined);
};

export const waitToken = async () => {
  let token = getToken();
  while (!_isStr(token) || token.length < 20) {
    console.log("getToken retrying to get token", token);
    await sleep(1);
    token = getToken();
  }
  console.log("getToken got a token string", token);
  return token;
};

export const updateOldCache = (lastUpdate, triggerCacheUpdate) => {
  if (Moment().diff(Moment(lastUpdate), "hours") > 24) {
    triggerCacheUpdate();
  }
};

export const getCompanyHeaders = (companyId) => {
  return {
    "X-ActionSustainability-Company-Id": companyId,
    "X-ActionSustainability-Company-Name": companyId,
  };
};

export const doFetch = async (
  url,
  method = "GET",
  data = {},
  _headers = {},
) => {
  const token = await waitToken();
  const companyHeaders = getCompanyHeaders();

  const headers = {
    Authorization: `JWT ${token}`,
    ...(data instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...companyHeaders,
    ..._headers,
  };
  console.log("Calling API", method, url, headers);

  const response = await fetch(url, {
    method,
    headers,
    body:
      method == "GET"
        ? null
        : data instanceof FormData
          ? data
          : JSON.stringify(data),
  });

  return response;
};

export const fetcher = async (
  url,
  method = "GET",
  data = {},
  _headers = {},
) => {
  const response = await doFetch(url, method, data, _headers);

  const _data = await response.json();
  const responseHeaders = Object.fromEntries(response.headers.entries());
  console.log("Got API response from", url, _data, responseHeaders);

  return {
    payload: Im.fromJS(_data),
    headers: responseHeaders,
  };
};

const swrOptions = {
  revalidate: false,
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

const swrMutateOptions = {
  revalidate: false,
  populateCache: true,
};

const urlFromArgs = (url, args) => {
  if (!url) {
    return url;
  }
  const requiredArgs = url.matchAll(/{(.*?)}/g);
  let _url = `${url}`;
  for (const [param, key] of requiredArgs) {
    if (!args || !args[key]) {
      return null;
    }
    _url = _url.replaceAll(param, args[key]);
  }
  return _url;
};

export default function useApi(url, args, genArgs) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastUpdate, setLastUpdate] = useState("");

  const _url = urlFromArgs(url, args);
  // console.log("Executing useApi", url, _url == url ? "" : _url);

  const props = useSWR(_url, fetcher, swrOptions);
  const { trigger, isMutating } = useSWRMutation(
    url,
    () => fetcher(url),
    swrMutateOptions,
  );
  const { trigger: triggerCacheUpdate, isMutating: isRefreshingCache } =
    useSWRMutation(
      url,
      () => fetcher(url, "GET", {}, { "cache-force-update": true }),
      swrMutateOptions,
    );

  const { data: _data, isLoading, error } = props;
  const isError = !isLoading && !!error;
  const { payload: data, headers } = _data || {};

  useEffect(() => {
    // Update the lastUpdate from response headers
    const _lastUpdate = (headers || {})["cache-last-update"];
    if (_lastUpdate) {
      setLastUpdate(new Moment(_lastUpdate).toISOString());
    }
    console.log(`useApi updated lastUpdate (lastUpdate = ${_lastUpdate})`);

    // Try in the retry-after seconds
    const retry = (headers || {})["retry-after"];
    console.log(`useApi executing useEffect (retry-after = ${retry})`);
    if (retry) {
      console.log("useApi retrying again after", retry);
      setIsRetrying(true);
      setTimeout(() => {
        console.log("triggering useApi again");
        trigger();
      }, retry * 1000);
    } else {
      setIsRetrying(false);
    }
  }, [data]);

  return {
    ...props,
    data,
    headers,
    url: _url,
    isError,
    args: data && !isError && genArgs ? genArgs(data) : null,
    trigger,
    triggerCacheUpdate: () => {
      setIsRetrying(true);
      triggerCacheUpdate();
    },
    isMutating,
    isFetching: isLoading || isMutating,
    isRetrying,
    lastUpdate,
  };
}

const getFetchArgs = (fetches) =>
  Object.values(fetches)
    .map((i) => i.args)
    .filter((i) => i)
    .reduce((acc, cur) => ({ ...acc, ...cur }), {});

export function useApis(fetches, updateParams) {
  const args = getFetchArgs(fetches);
  if (updateParams) {
    updateParams(args);
  }
  console.log("useApis has arguments", args);
  return {
    fetches,
    urls: Object.values(fetches).map((i) => i.url),
    isLoading: () => Object.values(fetches).some((i) => i.isLoading),
    isMutating: () => Object.values(fetches).some((i) => i.isMutating),
    isFetching: () => Object.values(fetches).some((i) => i.isFetching),
    mutate: (keys) =>
      (keys || Object.keys(fetches)).map((key) => mutate(fetches[key]?.url)),
    trigger: (keys) =>
      (keys || Object.keys(fetches)).map((key) => fetches[key]?.trigger()),
    args,
  };
}

const hasUpdates = (update = {}, obj = {}) => {
  console.log("Checking objects for updates", update, obj);
  const newKeys = Im.Set(Object.keys(update)).subtract(
    Im.Set(Object.keys(obj)),
  );
  if (newKeys.size) {
    return true;
  }
  for (const key of Object.keys(update)) {
    if (update[key] != obj[key]) {
      return true;
    }
  }
};

export function useApiParams(_params) {
  const [params, setParams] = useState(_params || {});

  const updateParams = (newParams) => {
    if (newParams && hasUpdates(newParams, params)) {
      console.log("Updating api parameter set");
      setParams({ ...params, ...newParams });
    }
  };

  return {
    params,
    updateParams,
    setParams,
  };
}

const postFetch = async (url, data) => {
  if (!url) {
    return null;
  }
  if (!data) {
    throw "No data provided for POST";
  }
  const response = await fetcher(url, "POST", data);
  return response.payload;
};

export function usePostCache(url, body) {
  const hashCode = Im.fromJS(body)?.hashCode();
  const props = useSWR([url, hashCode], () => postFetch(url, body), swrOptions);
  // console.log("usePostCache returning data: ", url, hashCode, props)
  return props;
}

export function RefreshButton({ mutate, spin }) {
  return (
    <span style={{ float: "right", marginRight: "1em" }}>
      Refreshing
      {/* {spin ? (
                <SyncOutlined spin={true} />
            ) : (
                <ReloadOutlined onClick={() => mutate()} />
            )} */}
    </span>
  );
}
